import React, { useContext } from 'react';

import { AutoLinkContext } from '@betterment/js-runtime';
import { SambaAutoLink } from '@betterment/shared-components';
import { Box, get as themeGet } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { NavLink as ReactNavLink, matchPath, useLocation } from 'react-router-dom';

export type NavLinkProps = {
  shouldReloadDocument?: boolean;
  to: string;
  isExact: boolean;
  label: string;
};

export type HeaderNavProps = {
  items: NavLinkProps[];
};

type LinkBaseProps = NavLinkProps & { className?: string };

const activeClassName = 'active-nav-item';

const LinkBase: React.FC<LinkBaseProps> = ({ shouldReloadDocument = false, to, isExact, label, className }) => {
  const location = useLocation();
  const shouldUseSambaAutoLink = Boolean(useContext(AutoLinkContext));

  if (shouldUseSambaAutoLink) {
    const toWithoutTrailingSlash = to.endsWith('/') && to.length > 1 ? to.slice(0, -1) : to;
    const matchTo = isExact ? toWithoutTrailingSlash : `${toWithoutTrailingSlash}/*`;
    const isActive = matchPath(matchTo, location.pathname);

    return (
      <SambaAutoLink to={to} className={isActive ? `${className} ${activeClassName}` : className}>
        {label}
      </SambaAutoLink>
    );
  }

  return (
    <ReactNavLink
      className={({ isActive }) => (isActive ? `${className} ${activeClassName}` : className)}
      to={to}
      end={isExact}
      reloadDocument={shouldReloadDocument}
    >
      {label}
    </ReactNavLink>
  );
};

const StyledNavLink = styled(LinkBase)<NavLinkProps>`
  margin-right: 3.2rem;
  padding-bottom: 1.6rem;
  padding-top: 1.6rem;

  && {
    color: ${themeGet('colors.bmtGrey50')};
  }

  &&.${activeClassName} {
    border-bottom: 2px solid ${themeGet('colors.bmtGrey90')};
    color: ${themeGet('colors.bmtGrey90')};
  }

  &:hover,
  &:active {
    text-decoration: none;
    color: ${themeGet('colors.bmtGrey90')};
    outline: none;
  }
  &:focus {
    outline: none;
    text-decoration: none;
  }
  &:focus-visible {
    outline: 2px solid ${themeGet('colors.bmtBlue')};
    outline-offset: 2px;
    text-decoration: none;
  }
`;

const HeaderNavLink: React.FC<NavLinkProps> = ({ to, isExact, label, shouldReloadDocument }) => (
  <StyledNavLink to={to} isExact={isExact} label={label} shouldReloadDocument={shouldReloadDocument} />
);

export const HeaderNav: React.FC<HeaderNavProps> = ({ items }) => (
  <Box pb={6} data-testid="headerNav">
    {items.map(itemProps => (
      <HeaderNavLink key={itemProps.label} {...itemProps} />
    ))}
  </Box>
);
